import { fetch_accessible_platforms, launch_platform } from './platform_access_service';
import { get_platform_config, FALLBACK_PLATFORM_COLOR } from './platforms_config';

const GRID_COLUMNS = 3;

export default {
  name: 'PlatformSwitcher',

  props: {
    current_platform_id: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    is_loading: true,
    has_error: false,
    is_open: false,
    platforms: [],
    active_index: -1,
    launching_id: null,
    launch_error: '',
  }),

  computed: {
    is_aic_one_user() {
      return localStorage.getItem('aic_one_login') === 'true';
    },

    current_platform() {
      return get_platform_config(this.current_platform_id) || {
        id: this.current_platform_id,
        name: this.current_platform_id,
        monogram: '?',
        color: FALLBACK_PLATFORM_COLOR,
      };
    },

    sorted_platforms() {
      return [...this.platforms].sort((a, b) => a.name.localeCompare(b.name));
    },
  },

  mounted() {
    if (!this.is_aic_one_user) return;
    document.addEventListener('click', this.on_outside_click);
    window.addEventListener('pageshow', this.handle_pageshow);
    this.fetch_platforms();
  },

  beforeUnmount() {
    document.removeEventListener('click', this.on_outside_click);
    window.removeEventListener('pageshow', this.handle_pageshow);
  },

  methods: {
    async fetch_platforms() {
      this.is_loading = true;
      this.has_error = false;
      try {
        this.platforms = await fetch_accessible_platforms(this.current_platform_id);
      } catch {
        this.has_error = true;
        this.platforms = [];
      } finally {
        this.is_loading = false;
      }
    },

    retry_fetch() {
      this.fetch_platforms();
    },

    badge_style(platform) {
      return ({
        backgroundColor: platform?.color || FALLBACK_PLATFORM_COLOR,
        color: '#fff',
      });
    },

    tile_style(platform) {
      return ({ '--tile-color': platform?.color || FALLBACK_PLATFORM_COLOR });
    },

    is_current_platform(platform) {
      return platform.id === this.current_platform_id;
    },

    toggle_menu() {
      if (this.is_loading) return;
      if (this.is_open) this.close_menu();
      else this.open_menu();
    },

    open_menu() {
      this.is_open = true;
      this.launch_error = '';
      this.active_index = -1;
      this.$nextTick(() => this.focus_item(0));
    },

    close_menu(refocus_trigger = false) {
      this.is_open = false;
      this.active_index = -1;
      if (refocus_trigger) this.$refs.trigger_button?.focus();
    },

    menu_item_refs() {
      const refs = this.$refs.platform_tile;
      if (Array.isArray(refs)) return refs;
      return refs ? [refs] : [];
    },

    focus_item(index) {
      const items = this.menu_item_refs();
      if (!items.length) return;
      const clamped = (index + items.length) % items.length;
      this.active_index = clamped;
      Reflect.get(items, clamped)?.focus();
    },

    on_menu_keydown(event) {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.close_menu(true);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.focus_item(this.active_index + 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.focus_item(this.active_index - 1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.focus_item(this.active_index + GRID_COLUMNS);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focus_item(this.active_index - GRID_COLUMNS);
          break;
        case 'Home':
          event.preventDefault();
          this.focus_item(0);
          break;
        case 'End':
          event.preventDefault();
          this.focus_item(this.menu_item_refs().length - 1);
          break;
        default:
          break;
      }
    },

    async select_platform(platform) {
      if (this.is_current_platform(platform)) {
        this.close_menu(true);
        return;
      }
      if (this.launching_id) return;

      this.launch_error = '';
      this.launching_id = platform.id;
      try {
        await launch_platform(platform);
      } catch (error) {
        this.launch_error = error.message || 'Unable to launch platform';
        this.launching_id = null;
      }
    },

    on_outside_click(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close_menu();
    },

    // A launch navigates via window.location.assign, which never lets this
    // component unmount cleanly. If the browser restores this page from
    // back/forward cache (e.g. the user hits Back from the platform they
    // launched), `launching_id` is still frozen "in flight" from before —
    // clear it so the tile doesn't show a permanently stuck spinner.
    handle_pageshow(event) {
      if (!event.persisted) return;
      this.launching_id = null;
      this.launch_error = '';
    },
  },
};

